import React, { useState, useEffect } from "react";
import OrdersTable from "./components/OrdersTable";
import OrderDetails from "./components/OrderDetails";
import { Order } from "./types";
import { AppLayout } from "src/components/layouts";
import { useOrderSSE } from "src/hooks/useOrderSSE";
import BackButton from "src/components/BackButton";
import {
  loadOrdersFromStorage,
  saveOrdersToStorage,
  checkAndClearExpiredOrders,
} from "src/utils/orderStorage";

const Orders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Load orders from localStorage on mount
  useEffect(() => {
    // Check and clear expired orders first
    checkAndClearExpiredOrders();

    // Load orders from localStorage
    const storedOrders = loadOrdersFromStorage();
    if (storedOrders.length > 0) {
      setOrders(storedOrders);
    }
  }, []);

  // Save orders to localStorage whenever orders change
  useEffect(() => {
    // Always save (even if empty) to update the date key
    saveOrdersToStorage(orders);
  }, [orders]);

  // Set up daily cleanup check (check every minute if date changed)
  useEffect(() => {
    const checkDateInterval = setInterval(() => {
      // Store previous date before checking
      const STORAGE_DATE_KEY = "peak_reach_orders_date";
      const previousDate = localStorage.getItem(STORAGE_DATE_KEY);

      // Check and clear expired orders
      checkAndClearExpiredOrders();

      // Check if date changed (orders were cleared)
      const currentDate = localStorage.getItem(STORAGE_DATE_KEY);
      if (previousDate && previousDate !== currentDate) {
        setOrders([]);
      } else {
        // Reload orders in case they were modified elsewhere
        const storedOrders = loadOrdersFromStorage();
        // Only update if there's a mismatch to avoid unnecessary re-renders
        if (storedOrders.length !== orders.length) {
          setOrders(storedOrders);
        }
      }
    }, 60000); // Check every minute

    return () => clearInterval(checkDateInterval);
  }, [orders.length]);

  // Helper function to transform SSE order to our Order interface
  const transformSSEOrder = (sseOrder: any): Order => {
    return {
      id: sseOrder.order_id || sseOrder.code || "",
      order_type: sseOrder.type === "parcel" ? "parcel" : "store",
      customer_name: sseOrder.customer_name || "N/A",
      customer_phone: sseOrder.customer_phone || "",
      delivery_address: sseOrder.delivery_address || sseOrder.message || "N/A",
      status: sseOrder.status || "pending",
      date: sseOrder.created_at || new Date().toISOString(),
      total_amount: sseOrder.total_amount || 0,
      items: sseOrder.items || [],
      restaurant_name: sseOrder.restaurant_name,
      pickup_address: sseOrder.pickup_address,
      parcel_type: sseOrder.parcel_type,
      weight: sseOrder.weight,
      rider: sseOrder.rider,
    };
  };

  // Setup SSE connection for real-time order updates (only between 8am and 10pm)
  useOrderSSE({
    onNewOrder: (sseOrder: any) => {
      const transformedOrder = transformSSEOrder(sseOrder);

      setOrders((prevOrders) => {
        // Check if order already exists to avoid duplicates
        const exists = prevOrders.some(
          (order) => order.id === transformedOrder.id
        );
        if (exists) {
          return prevOrders;
        }
        return [transformedOrder, ...prevOrders];
      });
    },
    onOrderUpdate: (sseOrder: any) => {
      const transformedOrder = transformSSEOrder(sseOrder);

      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.id === transformedOrder.id ? transformedOrder : order
        )
      );
      // Update selected order if it's the one being updated
      if (selectedOrder && selectedOrder.id === transformedOrder.id) {
        setSelectedOrder(transformedOrder);
      }
    },
    playSound: true,
    openingTime: "08:00",
    closingTime: "22:00",
  });

  const handleViewDetails = (order: Order) => {
    setSelectedOrder(order);
    setShowDetails(true);
  };

  const handleCloseDetails = () => {
    setShowDetails(false);
    setSelectedOrder(null);
  };

  // Function to update order status
  // const handleUpdateOrderStatus = (orderId: string, newStatus: string) => {
  //   setOrders((prevOrders) =>
  //     prevOrders.map((order) =>
  //       order.id === orderId ? { ...order, status: newStatus } : order
  //     )
  //   );

  //   // If the updated order is currently selected, update it
  //   if (selectedOrder && selectedOrder.id === orderId) {
  //     setSelectedOrder({ ...selectedOrder, status: newStatus });
  //   }
  // };

  return (
    <AppLayout>
      <div className="container mx-auto px-2 py-4">
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm p-2 overflow-x-hidden">
            {/* Top Section */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
              <div className="flex items-center gap-4">
                <BackButton />
                <h2 className="text-2xl font-bold text-amber-900">
                  Orders Management
                </h2>
              </div>
            </div>

            {/* Orders Table */}
            <OrdersTable
              orders={orders}
              onViewDetails={handleViewDetails}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
            />
          </div>
        </div>
      </div>

      {showDetails && selectedOrder && (
        <OrderDetails order={selectedOrder} onClose={handleCloseDetails} />
      )}
    </AppLayout>
  );
};

export default Orders;
